/*
 * Branded, in-app Cognito auth (email-first login) — Promise wrappers over
 * amazon-cognito-identity-js. Authentication uses USER_SRP_AUTH via
 * CognitoUserPool/CognitoUser; every network call is an XHR to
 * https://cognito-idp.eu-west-2.amazonaws.com/ — the browser NEVER visits
 * *.amazoncognito.com (no hosted UI, no redirect).
 *
 * Config comes from the existing env seam (readAuthConfig → pool/client/region);
 * nothing is invented here. The native AuthenticationResult maps 1:1 onto the
 * existing TokenSet, so session.ts stays the single session source of truth and
 * my-api + tickets bearer contracts accept these ID tokens unchanged (same
 * pool+clientId ⇒ same iss/aud/JWKS).
 *
 * The library is constructed with a custom IN-MEMORY Storage object so it never
 * writes its own localStorage keys — session.ts owns persistence.
 */
import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type CognitoUserSession,
  type ICognitoStorage,
} from 'amazon-cognito-identity-js'
import { readAuthConfig, type ConsumerAuthConfig } from './authConfig'
import { normalizeEmail, stripHiddenEmailChars } from './normalizeEmail'
import type { TokenSet } from './oidc'

// ---------------------------------------------------------------------------
// Normalised error surface
// ---------------------------------------------------------------------------

/** Normalised auth error codes — the UI maps ONLY these, never raw AWS names. */
export type AuthErrorCode =
  | 'UserNotFound'
  | 'NotAuthorized'
  | 'UserNotConfirmed'
  | 'PasswordResetRequired'
  | 'InvalidPassword'
  | 'InvalidParameter'
  | 'UsernameExists'
  | 'CodeMismatch'
  | 'ExpiredCode'
  | 'CodeDeliveryFailure'
  // EMAIL_OTP requires a verified email; an existing account whose email is
  // unverified is offered no EMAIL_OTP challenge (native USER_AUTH email-code
  // flow, src/auth/userAuth.ts). Surfaced distinctly so the UI is honest.
  | 'EmailUnverified'
  | 'LimitExceeded'
  | 'TooManyRequests'
  | 'Network'
  | 'Unknown'

export class AuthFlowError extends Error {
  readonly code: AuthErrorCode
  /** Seconds until a retry can succeed — set only for a my-api 429 that sent
   *  Retry-After (the account-route limiter); drives the login countdown. */
  readonly retryAfterSeconds?: number
  constructor(code: AuthErrorCode, message: string, retryAfterSeconds?: number) {
    super(message)
    this.name = 'AuthFlowError'
    this.code = code
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds
  }
}

const RAW_TO_CODE: Record<string, AuthErrorCode> = {
  UserNotFoundException: 'UserNotFound',
  NotAuthorizedException: 'NotAuthorized',
  UserNotConfirmedException: 'UserNotConfirmed',
  PasswordResetRequiredException: 'PasswordResetRequired',
  InvalidPasswordException: 'InvalidPassword',
  InvalidParameterException: 'InvalidParameter',
  UsernameExistsException: 'UsernameExists',
  CodeMismatchException: 'CodeMismatch',
  ExpiredCodeException: 'ExpiredCode',
  CodeDeliveryFailureException: 'CodeDeliveryFailure',
  LimitExceededException: 'LimitExceeded',
  TooManyRequestsException: 'TooManyRequests',
  // amazon-cognito-identity-js tags fetch failures err.code='NetworkError'
  // (lib/Client.js) — NOT an empty code. Without this row a real offline
  // refresh normalised to 'Unknown', which the session-end discriminators
  // (sessionExpiry.probeAndHeal, DataProvider.tryRefreshAndRefetch) read as
  // "session dead" → redirect while offline (adversarial review F2 — the
  // exact never-sign-out-on-a-blip behaviour CMT-2026-09-03-026 forbids).
  NetworkError: 'Network',
}

/** Normalise a raw library / network error into an AuthFlowError. The raw
 *  server message is PRESERVED verbatim so states like CREATE_PASSWORD can
 *  render Cognito's own password-policy text (spec: render server message). */
export function normaliseError(err: unknown): AuthFlowError {
  if (err instanceof AuthFlowError) return err
  const raw = err as { code?: string; name?: string; message?: string } | undefined
  const rawCode = raw?.code || raw?.name || ''
  const message = raw?.message || 'Something went wrong. Please try again.'
  const code = RAW_TO_CODE[rawCode] ?? (rawCode === '' ? 'Network' : 'Unknown')
  return new AuthFlowError(code, message)
}

// ---------------------------------------------------------------------------
// In-memory storage + pool/user construction (no localStorage writes)
// ---------------------------------------------------------------------------

class InMemoryStorage implements ICognitoStorage {
  private store: Record<string, string> = {}
  getItem(key: string): string | null {
    return Object.prototype.hasOwnProperty.call(this.store, key) ? this.store[key] : null
  }
  setItem(key: string, value: string): void {
    this.store[key] = value
  }
  removeItem(key: string): void {
    delete this.store[key]
  }
  clear(): void {
    this.store = {}
  }
}

function requireConfig(): ConsumerAuthConfig {
  const config = readAuthConfig()
  if (!config) throw new AuthFlowError('Unknown', 'auth not configured')
  return config
}

interface AuthCtx {
  storage: ICognitoStorage
  pool: CognitoUserPool
}

function makeCtx(config: ConsumerAuthConfig): AuthCtx {
  const storage = new InMemoryStorage()
  const pool = new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.userPoolClientId,
    Storage: storage,
  })
  return { storage, pool }
}

function makeUser(config: ConsumerAuthConfig, email: string): { user: CognitoUser; ctx: AuthCtx } {
  const ctx = makeCtx(config)
  const user = new CognitoUser({ Username: email, Pool: ctx.pool, Storage: ctx.storage })
  return { user, ctx }
}

/** Map a native CognitoUserSession onto the existing TokenSet. expiresIn is
 *  derived from the ID token's own expiry (getExpiration is epoch SECONDS). */
export function sessionToTokens(session: CognitoUserSession, now: number = Date.now()): TokenSet {
  const idToken = session.getIdToken()
  const nowSec = Math.floor(now / 1000)
  const expiresIn = Math.max(0, idToken.getExpiration() - nowSec)
  const refresh = session.getRefreshToken()?.getToken()
  return {
    idToken: idToken.getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: refresh || undefined,
    expiresIn,
  }
}

// ---------------------------------------------------------------------------
// Flows
// ---------------------------------------------------------------------------

/** newPasswordRequired is surfaced as a TYPED result, not a throw. `pending`
 *  carries the live CognitoUser so completeNewPassword can answer the same
 *  challenge. */
export interface NewPasswordPending {
  user: CognitoUser
  requiredAttributes: string[]
}

export type SignInResult =
  | { status: 'ok'; tokens: TokenSet }
  | { status: 'newPasswordRequired'; pending: NewPasswordPending }

/**
 * Sign in with the canonical (lowercase) username FIRST; on a NotAuthorized /
 * UserNotFound failure retry ONCE with the as-typed casing (Duncan checklist
 * #3, BOARDROOM-2026-09-02-191616).
 *
 * WHY: the consumer pool is case-SENSITIVE and legacy usernames were minted
 * as-typed, so unconditional lowercasing (the previous behaviour) LOCKED OUT
 * every mixed-case-username legacy account. Canonical-first order makes the
 * lowercase account win deterministically for the live duplicate pairs (both
 * variants CONFIRMED) whenever the entered password matches it. When the
 * canonical attempt fails, the as-typed retry usually rescues a legacy
 * mixed-case account the canonical attempt could not see — but if BOTH
 * variants exist as a duplicate pair, a password matching only the as-typed
 * twin DOES sign into that twin. Reviewed and KEPT (PR#128 review P3-1):
 * the user proved that twin's credential, and refusing would lock the twin
 * out with nothing gained; the canonical account still wins every tie.
 *
 * Error surfacing: UserNotConfirmed / PasswordResetRequired on the FIRST
 * attempt prove the canonical account exists → no retry (real state, not a
 * miss). If the retry ALSO fails with NotAuthorized/UserNotFound, the FIRST
 * (canonical) error is thrown — the canonical identity is authoritative; a
 * retry failure carrying real account state (e.g. the as-typed account is
 * unconfirmed) is surfaced instead. Cost: one extra SRP round-trip, only on a
 * failed attempt with mixed-case input; the retry targets a DIFFERENT
 * username, so it never advances the canonical account's lockout counter.
 */
export function signIn(email: string, password: string): Promise<SignInResult> {
  const canonical = normalizeEmail(email)
  const asTyped = stripHiddenEmailChars(email)
  return signInAs(canonical, password).catch((err: unknown) => {
    const first = normaliseError(err)
    const missable = first.code === 'NotAuthorized' || first.code === 'UserNotFound'
    if (!missable || asTyped === canonical) throw first
    // One fallback, logged (no PII beyond what the user just typed themselves).
    console.info('[auth] canonical sign-in did not match — retrying once with the as-typed username (legacy mixed-case account fallback)')
    return signInAs(asTyped, password).catch((retryErr: unknown) => {
      const second = normaliseError(retryErr)
      throw second.code === 'NotAuthorized' || second.code === 'UserNotFound' ? first : second
    })
  })
}

/** A password sign-in that has not settled after this long is reported, never
 *  left spinning (SIGNIN_STALL). Two SRP round trips take well under a second. */
export const SIGNIN_STALL_MS = 20_000

const SIGNIN_INCOMPLETE_COPY =
  'We couldn’t finish signing you in. Please try again, or use “Email me a sign-in code”.'

/**
 * Callbacks for every challenge my.trumin does not complete in the password
 * flow (TOTP / SMS MFA, MFA setup, MFA type choice, custom auth). The library
 * CALLS these by name; a missing one threw a TypeError inside its own XHR
 * callback, so the sign-in promise never settled and the Login screen stayed
 * on "Signing in…" with no message (quality real-form leg, 2026-10-30: Cognito
 * RespondToAuthChallenge 200, then nothing). Each one now rejects visibly and
 * logs the challenge name only.
 */
function unsupportedChallengeCallbacks(reject: (e: AuthFlowError) => void) {
  const refuse = (name: string) => {
    console.warn(`[auth] sign-in stopped at an unsupported Cognito challenge: ${name}`)
    reject(new AuthFlowError('Unknown', SIGNIN_INCOMPLETE_COPY))
  }
  return {
    mfaRequired: (challengeName: unknown) => refuse(String(challengeName || 'SMS_MFA')),
    totpRequired: (challengeName: unknown) => refuse(String(challengeName || 'SOFTWARE_TOKEN_MFA')),
    selectMFAType: () => refuse('SELECT_MFA_TYPE'),
    mfaSetup: () => refuse('MFA_SETUP'),
    customChallenge: () => refuse('CUSTOM_CHALLENGE'),
  }
}

/** Settle `run` with a visible error if it has not settled in SIGNIN_STALL_MS
 *  (the library can also throw inside its callback on a challenge it does not
 *  know at all, e.g. a future first-factor name, which no callback catches). */
function withStallGuard<T>(label: string, run: (resolve: (v: T) => void, reject: (e: unknown) => void) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let done = false
    const timer = setTimeout(() => {
      if (done) return
      done = true
      console.warn(`[auth] ${label} did not settle within ${SIGNIN_STALL_MS} ms — reporting it`)
      reject(new AuthFlowError('Unknown', SIGNIN_INCOMPLETE_COPY))
    }, SIGNIN_STALL_MS)
    const settle = <A>(fn: (a: A) => void) => (a: A) => {
      if (done) return
      done = true
      clearTimeout(timer)
      fn(a)
    }
    try {
      run(settle(resolve), settle(reject))
    } catch (e) {
      settle(reject)(e)
    }
  })
}

/** Single SRP attempt against ONE exact username — the composition seam for
 *  signIn's canonical-first / as-typed-retry policy above. */
function signInAs(username: string, password: string): Promise<SignInResult> {
  return withStallGuard<SignInResult>('password sign-in', (resolve, reject) => {
    const config = requireConfig()
    const { user } = makeUser(config, username)
    const details = new AuthenticationDetails({ Username: username, Password: password })
    user.authenticateUser(details, {
      onSuccess: (session) => resolve({ status: 'ok', tokens: sessionToTokens(session) }),
      onFailure: (err) => reject(normaliseError(err)),
      newPasswordRequired: (_userAttributes, requiredAttributes) => {
        const required = Array.isArray(requiredAttributes) ? requiredAttributes : []
        resolve({ status: 'newPasswordRequired', pending: { user, requiredAttributes: required } })
      },
      ...unsupportedChallengeCallbacks(reject),
    })
  })
}

/**
 * Exchange a stored refresh token for a FRESH session (Cognito
 * REFRESH_TOKEN_AUTH). The ID token minted at sign-in predates any later
 * profile save, so its `given_name`/`family_name` claims are stale (or absent
 * for a first-time profile) — the wallet stub then renders a blank participant
 * name (BOARDROOM-2026-08-14-100859). Refreshing re-mints the ID token from the
 * account's CURRENT attributes, so session.ts's claims carry the just-saved
 * name; the re-minted access token still carries the intrinsic self-service
 * scope, so it services Cognito GetUser/UpdateUserAttributes unchanged.
 *
 * `username` is only used by the library to key the CognitoUser (the refresh
 * itself is authenticated by the token) — pass the id-token `cognito:username`,
 * or the email/sub as a fallback. Deliberately NOT normalizeEmail'd: it is a
 * claim value, not a typed credential, and REFRESH_TOKEN_AUTH never transmits
 * a USERNAME parameter.
 */
export function refreshSession(username: string, refreshToken: string): Promise<TokenSet> {
  return new Promise((resolve, reject) => {
    const config = requireConfig()
    const { user } = makeUser(config, username)
    const token = new CognitoRefreshToken({ RefreshToken: refreshToken })
    user.refreshSession(token, (err, session) => {
      if (err || !session) return reject(normaliseError(err))
      resolve(sessionToTokens(session))
    })
  })
}

export function completeNewPassword(pending: NewPasswordPending, newPassword: string): Promise<TokenSet> {
  return withStallGuard<TokenSet>('new-password completion', (resolve, reject) => {
    pending.user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => resolve(sessionToTokens(session)),
      onFailure: (err) => reject(normaliseError(err)),
      ...unsupportedChallengeCallbacks(reject),
    })
  })
}

export interface SignUpResult {
  /** false ⇒ a confirmation code was emailed and CONFIRM_CODE is required. */
  userConfirmed: boolean
}

export function signUp(email: string, password: string): Promise<SignUpResult> {
  return new Promise((resolve, reject) => {
    const config = requireConfig()
    const { pool } = makeCtx(config)
    const username = normalizeEmail(email)
    // Email as username; the get-ready wizard collects name/profile later — NO
    // name/profile attributes are set here (spec).
    const attrs = [new CognitoUserAttribute({ Name: 'email', Value: username })]
    pool.signUp(username, password, attrs, [], (err, result) => {
      if (err || !result) return reject(normaliseError(err))
      resolve({ userConfirmed: result.userConfirmed })
    })
  })
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const config = requireConfig()
    const { user } = makeUser(config, normalizeEmail(email))
    user.confirmRegistration(code, true, (err) => {
      if (err) return reject(normaliseError(err))
      resolve()
    })
  })
}

export function resendCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const config = requireConfig()
    const { user } = makeUser(config, normalizeEmail(email))
    user.resendConfirmationCode((err) => {
      if (err) return reject(normaliseError(err))
      resolve()
    })
  })
}

/** Exact-username escape hatch for the forgot flow (checklist #3): the reset
 *  code MUST go to the probe-RESOLVED Cognito username. A sign-in-style retry
 *  is impossible here — PreventUserExistenceErrors makes ForgotPassword on a
 *  non-existent username a SIMULATED success (no error to retry on), so a
 *  lowercased mixed-case username silently never delivers a code. Default
 *  (exactUsername absent/false) normalises, preserving every legacy call site. */
export interface ExactUsernameOpts {
  /** true ⇒ the first argument is a resolved Cognito username: NFC + hidden
   *  characters removed only, case preserved. Pass the username from probeEmailAuthoritative. */
  exactUsername?: boolean
}

function wireUsername(email: string, opts: ExactUsernameOpts): string {
  return opts.exactUsername ? stripHiddenEmailChars(email) : normalizeEmail(email)
}

export function forgotPassword(email: string, opts: ExactUsernameOpts = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const config = requireConfig()
    const { user } = makeUser(config, wireUsername(email, opts))
    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      resolve()
    }
    user.forgotPassword({
      onSuccess: done,
      inputVerificationCode: done, // fires when the reset code is delivered
      onFailure: (err) => {
        if (settled) return
        settled = true
        reject(normaliseError(err))
      },
    })
  })
}

export function confirmForgotPassword(email: string, code: string, newPassword: string, opts: ExactUsernameOpts = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const config = requireConfig()
    // MUST target the same username forgotPassword sent the code to.
    const { user } = makeUser(config, wireUsername(email, opts))
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(normaliseError(err)),
    })
  })
}

/** Best-effort global sign-out. Uses the same direct cognito-idp JSON API idiom
 *  as cognitoProfile.ts (the library's globalSignOut needs a full reconstructed
 *  CognitoUserSession we do not hold on logout). Callers ignore failure. */
export async function globalSignOut(accessToken: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  const config = readAuthConfig()
  if (!config) return
  await fetchImpl(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.GlobalSignOut',
    },
    body: JSON.stringify({ AccessToken: accessToken }),
  })
}
